import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import UserService from "../Services/UserList";
import Swal from "sweetalert2";
import MainLayout from "./admin/MainLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  TextField,
  Box,
  Tooltip,
  IconButton,
  TableSortLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import { useDispatch, useSelector } from "react-redux";
import { fetchLiveUser } from "../redux/slices/notifSlice";
import { Add, BlockOutlined, Search } from "@mui/icons-material";
import { fetchRegion, fetchState, fetchUsers } from "../redux/slices/clientSlice";
import Select from "react-select";
import TableChartIcon from "@mui/icons-material/TableChart";
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import LoadingIndicator from "./utils/LoadingIndicator";
import { TableBodySkeleton } from "./utils/TableSkeleton";
import RoleSelect from "./utils/RoleSelect";
import BulkUploadUser from "./utils/BulkUploadUser";
import MultiSelectWithCheckbox from "./utils/MultiSelectWithCheckbox";
import MonthFY from "./MonthFY";
import YearFY from "./YearFY";
import FilterBar2 from "./admin/admin_dashboard/FilterBar2";

const roleOpt = [
  { label: "IC", value: 2 },
  { label: "BSP", value: 3 },
  { label: "DSP", value: 4 },
  { label: "BD", value: 5 },
  { label: "Sales Enabler", value: 6 },
  { label: "Dealer", value: 7 },
  { label: "Studio", value: 8 },
];

const FY_YEAR_OPTIONS = [
  { label: "2026-2027", value: "2026" },
  { label: "2025-2026", value: "2025" },
  { label: "2024-2025", value: "2024" },
  { label: "2023-2024", value: "2023" },
];

const STATUS_OPTIONS = [
  { label: "Active", value: "1" },
  { label: "Deactive", value: "0" },
];

export default function AdminUserList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchQuery, setSearchQuery] = useState(state && state?.search || "");
  const [month, setMonth] = useState(() => {
    if (state && state.months) return state.months;

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });

  const [roleId, setRoleId] = useState(() => {
    if (state?.role && roleOpt) {
      return roleOpt.filter((r) => String(r.value) === String(state.role) || r.label === state.role);
    }
    return [];
  });

  const [statusFilter, setStatusFilter] = useState(() => {
    if (state?.is_active !== undefined && state.is_active !== "") {
      return STATUS_OPTIONS.filter((s) => String(s.value) === String(state.is_active));
    }
    return [];
  });

  const [year, setYear] = useState(() => {
    if (state?.years?.length > 0) {
      const targetYrs = state.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });

  const [showLiveUsers, setShowLiveUsers] = useState(false);
  const [channelOpt, setChannelOpt] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { liveUser } = useSelector(state => state.notif);
  const { regionOpt, stateOpt } = useSelector((state) => state.adminOption);

  const initialFromDate = (() => {
    if (state?.years && state?.monthFrom !== undefined) {
      return `${state.years}-${String(state.monthFrom).padStart(2, "0")}-01`;
    }
    return null;
  })();

  const initialToDate = (() => {
    if (state?.years && state?.monthTo !== undefined) {
      const lastDay = new Date(Number(state.years), state.monthTo, 0).getDate();
      return `${state.years}-${String(state.monthTo).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    }
    return null;
  })();

  const [fromDate, setFromDate] = useState(
    state && initialFromDate ? initialFromDate : null
  );
  const [toDate, setToDate] = useState(
    state && initialToDate ? initialToDate : null
  );
  const [selectedRegion, setSelectedRegion] = useState(() => {
    if (state?.regions && state.regions.length > 0 && regionOpt) {
      return regionOpt.filter((reg, idx) =>
        state.regions.includes(idx) ||
        state.regions.includes(idx + 1) ||
        state.regions.some(val => String(val).toLowerCase() === String(reg.value).toLowerCase())
      );
    }
    return [];
  });

  const [selectedState, setSelectedState] = useState(() => {
    if (state?.states && state.states.length > 0 && stateOpt) {
      return stateOpt.filter((st, idx) =>
        state.states.includes(idx) ||
        state.states.includes(idx + 1) ||
        state.states.some(val => String(val).toLowerCase() === String(st.value).toLowerCase())
      );
    }
    return [];
  });

  const [sortBy, setSortBy] = useState({
    sort_by: "",
    order: "desc",
  });
  const { stateData, regionData, userOpt, count, status } = useSelector((state) => state.client);
  const counts = count || 0;

  useEffect(() => {
    UserService.GetChannels().then(response => {
      if (response && response.data) {
        setChannelOpt(response.data.map(ch => ({
          value: ch.id,
          label: ch.channel_name
        })));
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const isUsingDateRange = fromDate || toDate;
    if (!isUsingDateRange && (year.length === 0 || month.length === 0)) {
      return;
    }
    dispatch(
      fetchUsers({
        search: searchQuery,
        state: selectedState?.map((u) => u.value)?.join(',') || "",
        region: selectedRegion?.map((u) => u.value)?.join(',') || "",
        channel: selectedChannels?.map((u) => u.value)?.join(',') || "",
        from: fromDate || "",
        to: toDate || "",
        page: page,
        rowsPerPage: rowsPerPage,
        sort_by: sortBy.sort_by || "",
        sort_order: sortBy.order || "",
        role: roleId?.map((r) => r.value)?.join(',') || "",
        status: statusFilter?.length > 0 ? statusFilter[0].value : "",
        month: month?.join(',') || "",
        year: year?.map((y) => y.value)?.join(',') || "",
        showLiveUsers: showLiveUsers ? 1 : 0,
      })
    );
  }, [searchQuery, selectedRegion, selectedState, selectedChannels, fromDate, toDate, page, rowsPerPage, sortBy, roleId, month, year, statusFilter, showLiveUsers]);

  const isFirstRender = useRef(true);

  // Adjust months if year changes (e.g., past year allows all 12 months)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // If months were explicitly passed from navigation state, don't override them on first render
      if (state && state.months) return;
    }

    if (year && year.length > 0) {
      const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
      const currentCalendarMonth = new Date().getMonth() + 1;
      const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
      const currentYrStr = String(new Date().getFullYear());

      const hasCurrentYear = year.some((y) => String(y.value) === currentYrStr);

      if (!hasCurrentYear) {
        // If we select a past year, select all months automatically 
        // to make it easier to view the full past year data
        setMonth(fullFYMonths);
      } else {
        // If we select the current year, restrict to current months
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
      }
    }
  }, [year]);


  const filterConfigs = [
    { id: "region", label: "Region", options: regionOpt || [], selected: selectedRegion },
    { id: "state", label: "State", options: stateOpt || [], selected: selectedState },
    { id: "channel", label: "Channel", options: channelOpt || [], selected: selectedChannels },
    { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
    { id: "status", label: "Status", options: STATUS_OPTIONS, selected: statusFilter },
    { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
  ];

  const handleFilterChange = (filterId, nextSelected) => {
    switch (filterId) {
      case "region": setSelectedRegion(nextSelected); break;
      case "state": setSelectedState(nextSelected); break;
      case "channel": setSelectedChannels(nextSelected); break;
      case "role": setRoleId(nextSelected); break;
      case "status": setStatusFilter(nextSelected); break;
      case "year": 
        setYear(nextSelected); 
        if (nextSelected.length === 0) {
          setMonth([]);
        }
        break;
      default: break;
    }
  };

  useEffect(() => {
    if (document.visibilityState !== 'visible') return;
    dispatch(fetchLiveUser());
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      dispatch(fetchLiveUser());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadExcel = () => {
    const filteredData = userOpt?.map((user, i) => ({
      sr_no: i + 1,
      user_type: user.user_type,
      channel: user.channel_name || "N/A",
      emp_code: user.emp_code,
      name: user.name,
      last_name: user.last_name,
      role:
        user.role_id == 2
          ? "IC"
          : user.role_id == 3
            ? "BSP"
            : user.role_id == 4
              ? "DSP"
              : user.role_id == 5
                ? "BD"
                : user.role_id == 6
                  ? "Sales Enabler"
                  : user.role_id == 7
                    ? "Dealer"
                    : user.role_id == 8
                      ? "Studio"
                      : user.role_id,
      ic: user.lc,
      no_of_quotations: user.quotations_count,
      sales_enablers: user.salesEnablerName,
      email: user.email,
      phone: user.phone,
      region: user.city,
      state: user.state,
      live_at: user.live,
      created_at: user.created_at,
      user_update: user.user_update,
      is_active: user.is_active == 1 ? "Active" : "Deactive",
    }));
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "UserList.xlsx");
  };
  const handleResetPassword = async (data) => {
    try {
      const response = await UserService.DefaultPassword({ id: data });

      if (response) {
        Swal.fire({
          icon: "success",
          title: "user",
          text: "password update successfully",
          timer: 3000,
        });
        navigate("/admin/userlist");
      }
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "warning",
        text: error.errors,
        timer: 3000,
      });
    }
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFromDate(null);
    setToDate(null);
    setSelectedRegion([]);
    setSelectedState([]);
    setSelectedChannels([]);
    setRoleId([]);
    setStatusFilter([]);
    setSortBy({ sort_by: "", order: "desc" });

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  };


  return (
    <MainLayout title={'Users'}>
      {status === "pending" && null}
      <FilterBar2
        filters={filterConfigs}
        onFilterChange={handleFilterChange}
        selectedMonth={month}
        setSelectedMonth={setMonth}
        searchTerm={searchQuery}
        setSearchTerm={setSearchQuery}
        selectedFromDate={fromDate}
        setSelectedFromDate={setFromDate}
        selectedToDate={toDate}
        setSelectedToDate={setToDate}
        onClearFilters={handleClearFilters}
        actions={
          <div className="flex items-center gap-2">
            <FormControlLabel
              control={
                <Switch
                  checked={showLiveUsers}
                  onChange={(e) => setShowLiveUsers(e.target.checked)}
                  color="success"
                  size="small"
                />
              }
              label={<span style={{ fontSize: '13px', fontWeight: 'bold' }}>Include All Live Users</span>}
              sx={{ color: 'white', mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
            />
            <Button
              variant="contained"
              startIcon={<TableChartIcon />}
              onClick={handleDownloadExcel}
              sx={{
                textTransform: "none",
                bgcolor: "#2e7d32",
                  py: '2px',
                  px: '14px',
                  borderRadius: 12,
                  fontSize: "12px",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              Excel
            </Button>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/admin/adduser")}
              sx={{
                textTransform: "none",
                bgcolor: "#2f5370ff",
                whiteSpace: "nowrap",
                  py: '2px',
                  px: '14px',
                  borderRadius: 12,
                  fontSize: "12px",
                "&:hover": { bgcolor: "#256692ff" },
              }}
            >
              Add User
            </Button>
            <BulkUploadUser />
          </div>
        }
      />

      <Paper elevation={3} style={{}}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 240px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ background: "#04121f", color: "white", "& th": { backgroundColor: "#04121f", color: "white" } }}>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  SL No.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >

                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  User Type
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Channel
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Emp Code/GST No.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "first_name"}
                    direction={
                      sortBy.sort_by === "first_name" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("first_name")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    First Name
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "last_name"}
                    direction={
                      sortBy.sort_by === "last_name" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("last_name")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "role_id"}
                    direction={
                      sortBy.sort_by === "role_id" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("role_id")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  IC
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  No. of Quotations
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Sales Enablers
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Email
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Phone
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "region"}
                    direction={
                      sortBy.sort_by === "region" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("region")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    Region
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "state"}
                    direction={
                      sortBy.sort_by === "state" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("state")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    State
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "live"}
                    direction={
                      sortBy.sort_by === "live" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("live")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Live At
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Live Time
                </TableCell>

                {/* created at */}
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "created_at"}
                    direction={
                      sortBy.sort_by === "created_at" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("created_at")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    Create At
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Time
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "user_update"}
                    direction={
                      sortBy.sort_by === "user_update" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("user_update")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >

                    Updated At
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Update Time
                </TableCell>
                {/* end */}
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sortBy.sort_by === "status"}
                    direction={
                      sortBy.sort_by === "status" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("status")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const isUsingDateRange = fromDate || toDate;
                const hasValidFilters = isUsingDateRange || (year?.length > 0 && month?.length > 0);
                if (!hasValidFilters) {
                  return (
                    <TableRow>
                      <TableCell colSpan={24} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  );
                }
                return userOpt?.length > 0 ? (
                  userOpt?.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell align="center">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="center">
                      {
                        liveUser?.some(e => e.emp_code == item.emp_code)
                          ? <OnlinePredictionIcon style={{ color: "green" }} />
                          : <OnlinePredictionIcon style={{ color: "red" }} />
                      }
                    </TableCell>
                    <TableCell align="center">{item.user_type}</TableCell>
                    <TableCell align="center">{item.channel_name || 'N/A'}</TableCell>
                    <TableCell align="center">{item.emp_code}</TableCell>
                    <TableCell align="center">{item.name}</TableCell>
                    <TableCell align="center">{item.last_name}</TableCell>
                    <TableCell align="center">
                      {item.role_id == 2
                        ? "IC"
                        : item.role_id == 3
                          ? "BSP"
                          : item.role_id == 4
                            ? "DSP"
                            : item.role_id == 5
                              ? "BD"
                              : item.role_id == 6
                                ? "Sales Enabler"
                                : item.role_id == 7
                                  ? "Dealer"
                                  : item.role_id == 8
                                    ? "Studio"
                                    : item.role_id}
                    </TableCell>

                    {/* <TableCell align="center">{currentUsers?.find(u=>u.id==item.designation)?.name}</TableCell> */}
                    <TableCell align="center">{item.lc}</TableCell>
                    <TableCell align="center">
                      <span 
                        className="font-bold text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => navigate('/admin/projects', { state: { users: [item.emp_code] } })}
                      >
                        {item.quotations_count || 0}
                      </span>
                    </TableCell>
                    <TableCell align="center">{item.salesEnablerName}</TableCell>
                    <TableCell align="center">{item.email}</TableCell>
                    <TableCell align="center">{item.phone}</TableCell>
                    <TableCell align="center">{item.city}</TableCell>
                    <TableCell align="center">{item.state}</TableCell>
                    <TableCell align="center">
                      {item.live ? new Date(item.live).toLocaleDateString("en-GB") : ""}
                    </TableCell>
                    <TableCell align="center">
                      {item.live? new Date(item.live).toLocaleString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }):""}
                    </TableCell>
                    <TableCell align="center">
                      {item ? new Date(item.created_at).toLocaleDateString("en-GB") : ""}
                    </TableCell>
                    <TableCell align="center">
                      {new Date(item.created_at).toLocaleString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </TableCell>
                    <TableCell align="center">
                      {item ? new Date(item.user_update).toLocaleDateString("en-GB") : ""}
                    </TableCell>
                    <TableCell align="center">
                      {new Date(item.user_update).toLocaleString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </TableCell>
                    <TableCell align="center">
                      {item.is_active == 1 ? (
                        <span style={{ color: "green" }}>Active</span>
                      ) : (
                        <span style={{ color: "red" }}>Deactive</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <span className="flex gap-3">
                        <Tooltip title="Edit User" arrow>
                          <IconButton
                            variant="contained"
                            color="primary"
                            component={Link}
                            to={`/admin/edit/${item.id}`}
                          >
                            <EditIcon className="text-yellow-400" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Reset Password" arrow>
                          <IconButton
                            variant="contained"
                            color="primary"
                            onClick={() => handleResetPassword(item.id)}
                          >
                            <LockResetIcon
                              fontSize="large"
                              className="text-red-400"
                            />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </TableCell>
                  </TableRow>
                )
                )
              ) : status === 'pending' ? (
                <TableBodySkeleton cols={24} rows={10} hasActions />
              ) : (
                <TableRow>
                  <TableCell colSpan={24} align="center">
                    No users found
                  </TableCell>
                </TableRow>

              );
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            marginTop: "1rem",
          }}
          rowsPerPageOptions={[10, 20, 50, 100, counts]}
          component="div"
          count={counts}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </MainLayout>
  );
}

const customStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    width: 200,
    borderColor: state.isFocused ? "#0288d1 !important" : "#c4c4c4",
    boxShadow: state.isFocused ? "0 0 0 1px #0288d1" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#0288d1" : "#a0a0a0",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#a0a0a0",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    width: 200,

  }),
};